import PropTypes from 'prop-types';
import { NavHandler, NavItem, VerticalNavbar } from '@nike/eds';
import { NikeApp } from '@nike/nike-design-system-icons';
import {
  Outlet,
  useNavigate,
  useLocation,
  redirect,
  useLoaderData,
} from 'react-router-dom';
import TopNav from './topNav';
import {
  setActiveNavItemsByCurrentUrlPath,
  navigationList,
} from '../utils/navigationHelpers';
import {
  decodeJwt,
  getAccessTokenFromLocalStorage,
} from '../utils/authHelpers';
import { QueryClient } from '@tanstack/react-query';
import { LOCAL_STORAGE_KEY } from '../constants';
import { useMemo } from 'react';

export const authLoader = (queryClient: QueryClient) => async () => {
  const jwt = getAccessTokenFromLocalStorage();

  if (!jwt) {
    if (window.localStorage && window.location) {
      localStorage.setItem(
        LOCAL_STORAGE_KEY.LOCATION_BEFORE_AUTH,
        window.location.pathname + window.location.search,
      );
    }
    console.log('No jwt from storage, redirecting to login page');
    return redirect('/login');
  }

  return { access_token: jwt };
};

function useAllowedNavigationList() {
  const loaderData = useLoaderData() as { access_token: string };
  const jwt = loaderData.access_token;
  const claim = decodeJwt(jwt);
  // filter out allowed navigation item
  return useMemo(() => {
    const userScopes: string[] = claim.scp || [];
    return navigationList.filter((item) => {
      // if nav item does not contains "scopes" assume it's allowed to access by "anyone"
      if (!item.scopes || item.scopes.length === 0) return true;
      // only consider the top level for now
      return item.scopes.some((scp) => userScopes.includes(scp));
    });
  }, [claim.scp]);
}

export default function RootLayout() {
  const loc = useLocation();
  const navigate = useNavigate();
  const navList = useAllowedNavigationList();
  setActiveNavItemsByCurrentUrlPath(navList, loc.pathname);
  const handleClick = (item: NavigationItem) => {
    navigate(item.link);
  };

  return (
    <main className='tw-flex tw-items-stretch tw-content-stretch'>
      <VerticalNavbar
        navLogoSlot={<NikeApp width={'56px'} height={'56px'} />}
        appName='Search 360'
        appSubtitle='Search Optimization&Troubleshoot Toolkit'
        onNavigate={handleClick as NavHandler}
        items={navList as NavItem[]}
        className='tw-min-h-screen'
      />
      <div
        id='layout-content'
        className='tw-bg-gray-100'
        style={{
          width: 'calc(100vw - 104px)',
          height: '100vh',
          overflow: 'auto',
        }}
      >
        <TopNav />
        <Outlet />
      </div>
    </main>
  );
}

RootLayout.propTypes = {
  children: PropTypes.any,
};
